import { Button } from '@storeblocks/button';
import { type FC } from 'react';
import { styled } from 'styled-components';

import { Esg } from '@/api/esg/models/Esg';
import { useOrganizationCmId } from '@/api/userSettings/hooks/useOrganizationCmId';
import { Center } from '@/components/Center';
import { Gauge } from '@/components/Charts';
import { Column, Row } from '@/components/Grid';
import { InternalLink } from '@/components/InternalLink';
import { ConnectSurface } from '@/components/Pretzel/ConnectSurface';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';
import { useUserLocale } from '@/i18n/hooks';
import { EsgFundList } from '@/pages/Esg/widgets/EsgFundList/EsgFundList';
import { TrackingElement, useGetTrackId } from '@/tracking';

interface Props {
  esgData: Esg;
  portfolioGroupId?: string;
}

export const EsgOverview: FC<Props> = ({ esgData, portfolioGroupId }) => {
  const organizationCmId = useOrganizationCmId();
  const locale = useUserLocale();
  const getTrackId = useGetTrackId();
  const texts = useTexts();

  return (
    <WithGap>
      <h2>{texts.dashboard.widgets.esgOverview.title}</h2>
      <Row data-test="esg-overview-widget">
        <StyledColumn $span={6}>
          <StyledSurface>
            <WithGap gap="48">
              <Center horizontal vertical>
                <Gauge
                  percent={esgData.solutionCompanies.kpi.value}
                  description={texts.pages.esg.overview.sustainableInvestments}
                  size={300}
                />
              </Center>

              <List>
                <ListItem>
                  <div className="font-body">
                    {texts.pages.esg.overview.carbonFootprint}
                  </div>
                  <span>
                    {`${esgData.pai11GhgEmissionsSum?.kpi.value.toLocaleString(locale)} ${texts.pages.esg.carbonFootprint.tonnage}`}
                  </span>
                </ListItem>
              </List>
            </WithGap>
            <div>
              <Button
                as={InternalLink}
                variant="tertiary"
                iconRight="arrow_right"
                to={`/${organizationCmId}/sustainability/esg${
                  portfolioGroupId ? `/${portfolioGroupId}` : ''
                }`}
                data-trackid={getTrackId('go-to-fund', TrackingElement.Link)}
                data-trackid-source="dashboard-page"
              >
                {texts.pages.esg.overview.goToEsgDashboard}
              </Button>
            </div>
          </StyledSurface>
        </StyledColumn>

        <StyledColumn $span={6}>
          <EsgFundList portfolioGroupId={esgData.portfolioGroupId} />
        </StyledColumn>
      </Row>
    </WithGap>
  );
};

const List = styled.ul`
  list-style-type: none;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-left: 0px;
`;

const ListItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--p-color-stroke-subtle-0);

  & > *:first-child {
    // Make the first element push the rest of
    // the elements to the right. I.e. make the
    // first element be left aligned and the rest
    // of the elements be right aligned.
    flex-grow: 1;
  }
`;

const StyledColumn = styled(Column)`
  display: flex;
`;

const StyledSurface = styled(ConnectSurface)`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: space-between;
`;
